import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef
} from 'react';
import ChatbotContext from './ChatbotContext';
import Chatbot from '../../types/Chatbot';
import Organisation from '../../types/Organisation';
import UserContext from '../UserContext/UserContext';
import BotBuilderService from '../../services/BotBuilderService/BotBuilderService';
import UserService from '../../services/UserService/UserService';

interface ChatbotProviderProps {
  children: React.ReactNode;
}

function ChatbotProvider({
  children
}: ChatbotProviderProps): React.ReactElement {
  const { user } = useContext(UserContext);
  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [currentChatbot, setCurrentChatbot] = useState<Chatbot | null>(null);
  const [selectedOrganisation, setSelectedOrganisation] =
    useState<Organisation | null>(null);
  const [initialized, setInitialized] = useState(false);
  const hasInitializedRef = useRef(false);
  const currentChatbotIdRef = useRef<number | null>(null);
  const [chatbotsRefreshKey, setChatbotsRefreshKey] = useState(0);

  const updateCurrentChatbot = useCallback(
    async (chatbotId: number | null) => {
      if (chatbotId === null) {
        currentChatbotIdRef.current = null;
        setCurrentChatbot(null);
        return;
      }

      const bot = await BotBuilderService.getChatbot(chatbotId);
      currentChatbotIdRef.current = bot.id;
      setCurrentChatbot(bot);

      if (user?.id) {
        await UserService.updateUserPreferences(chatbotId);
      }
    },
    [user?.id]
  );

  const refreshChatbots = useCallback(async () => {
    if (user?.organisationId == null) return;

    const chatbotResponse = await BotBuilderService.getChatbotsInfo(
      user.organisationId
    );
    setChatbots(chatbotResponse);

    if (currentChatbotIdRef.current) {
      const refreshed = await BotBuilderService.getChatbot(
        currentChatbotIdRef.current
      );
      setCurrentChatbot(refreshed);
    }

    setChatbotsRefreshKey((key) => key + 1);
  }, [user?.organisationId]);

  useEffect(() => {
    if (!user?.mfaComplete || user.organisationId == null) return;

    (async (): Promise<void> => {
      try {
        const chatbotResponse = await BotBuilderService.getChatbotsInfo(
          user.organisationId
        );

        setChatbots(chatbotResponse);

        if (!hasInitializedRef.current) {
          hasInitializedRef.current = true;
          if (user.selectedChatbotId) {
            await updateCurrentChatbot(user.selectedChatbotId);
          } else if (chatbotResponse.length > 0) {
            await updateCurrentChatbot(chatbotResponse[0].id);
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error loading chatbot summary:', err);
      } finally {
        setInitialized(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.mfaComplete, user?.organisationId]);

  const value = React.useMemo(
    () => ({
      chatbots,
      currentChatbot,
      setCurrentChatbot: updateCurrentChatbot,
      refreshChatbots,
      selectedOrganisation,
      setSelectedOrganisation,
      initialized,
      chatbotsRefreshKey
    }),
    [
      chatbots,
      currentChatbot,
      updateCurrentChatbot,
      refreshChatbots,
      selectedOrganisation,
      initialized,
      chatbotsRefreshKey
    ]
  );

  return (
    <ChatbotContext.Provider value={value}>{children}</ChatbotContext.Provider>
  );
}

export default ChatbotProvider;
